import { lazy, ComponentType } from 'react';
import { logSecurityEvent } from '@/utils/securityLogger';
import { normalizeError } from '@/utils/normalizeError';

type ComponentImportFn<T> = () => Promise<{ default: T }>;

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
// Guards against a reload loop if fresh chunks still fail after a hard reload.
const RELOAD_GUARD_KEY = 'chunk-reload-attempted';


/**
 * A wrapper around React.lazy that adds retry logic for failed chunk loads.
 * This is particularly useful for iOS WebView which aggressively caches old chunk references.
 * 
 * @param componentImport - A function that returns a dynamic import
 * @param retries - Number of retry attempts (default: 3)
 * @returns A lazy-loaded component with retry logic
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  componentImport: ComponentImportFn<T>,
  retries: number = MAX_RETRIES
) {
  return lazy(async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Add cache-busting query param on retry attempts
        if (attempt > 0) {
          console.log(`[lazyWithRetry] Retry attempt ${attempt + 1}/${retries}`);
        }
        
        const component = await componentImport();
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
        return component;
      } catch (error) {
        lastError = error as Error;
        console.warn(`[lazyWithRetry] Failed to load chunk (attempt ${attempt + 1}/${retries}):`, error);
        
        // Don't wait after the last attempt
        if (attempt < retries - 1) {
          // Exponential backoff: 1s, 2s, 4s...
          const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Try to clear module cache by adding timestamp to bust cache
          // This forces the browser to fetch fresh chunks
          if ('caches' in window) {
            try {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
              console.log('[lazyWithRetry] Cleared browser caches');
            } catch (cacheError) {
              console.warn('[lazyWithRetry] Failed to clear caches:', cacheError);
            }
          }
        }
      }
    }

    // All retries failed. Log with enough detail to tell "stale deploy" apart
    // from "hosting problem", then hard-reload once instead of surfacing the
    // error boundary flash to the user.
    console.error('[lazyWithRetry] All retry attempts failed');

    const normalized = normalizeError(lastError);
    const alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY);

    try {
      await logSecurityEvent(
        'client_chunk_load_error',
        null,
        normalized.error_message || 'Chunk load failed after retries',
        {
          ...normalized,
          retries: retries,
          chunk_url:
            (lastError as { request?: string } | undefined)?.request ?? null,
          app_version: import.meta.env.VITE_APP_VERSION ?? null,
          page_url: window.location.pathname,
          reload_attempted: !alreadyReloaded,
        }
      );
    } catch {
      // never block recovery on logging
    }

    if (!alreadyReloaded) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.replace(
        window.location.origin +
          window.location.pathname +
          '?_bust=' +
          Date.now()
      );
      // Keep the promise pending while the reload happens so React never
      // renders the error state.
      await new Promise(() => {});
    }

    throw lastError || new Error('Failed to load component after retries');
  });
}


export default lazyWithRetry;
